import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import { DashboardLayout } from '@components/layout/DashboardLayout.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { BarChart as MetricsIcon, CalendarMonth as CalendarIcon, Dashboard as DashboardIcon, Person as PersonIcon, Psychology as PsychologyIcon, Flag as FlagIcon, OutlinedFlag as OutlinedFlagIcon } from '@mui/icons-material';
import { PmProfileSidebar, PmProfileTab } from '@components/pm/PmProfileSidebar.tsx';
import { PmClientDashboardTab } from '@components/pm/client/PmClientDashboardTab.tsx';
import { PmClientProfileTab } from '@components/pm/client/PmClientProfileTab.tsx';
import { PmClientMetricsTab } from '@components/pm/client/PmClientMetricsTab.tsx';
import { PmClientCalendarTab } from '@components/pm/client/PmClientCalendarTab.tsx';
import { PmClientAiContextTab } from '@components/pm/client/PmClientAiContextTab.tsx';
import { PmClientGoalsTab } from '@components/pm/client/PmClientGoalsTab.tsx';
import { PmClientDailyNormsTab } from '@components/pm/client/PmClientDailyNormsTab.tsx';
import { pmUsersService } from '@services/pm-users.service.ts';
import { ClientDetails } from '@/types/client';

// Картка клієнта для продакт-менеджера (2026-10-10): /pm/client/:id/dashboard|calendar|ai-context|user-goals|daily-norms.
// Спрощений клон експертської /client/:id (pages/expert/ClientPage.tsx) - той самий каркас з правим сайдбаром і меню,
// але окремий, щоб розвивати під продакта незалежно. Продакт бачить будь-якого клієнта (бек: GET pm/clients/:id).
// "Цілі користувача" (user-goals) і "Денні норми" (daily-norms) - різні сутності,
// див. docs/technical-debt.md п.18 у api.
const TABS = [
  { path: 'dashboard', labelKey: 'clientProfile.tab.dashboard', icon: <DashboardIcon /> },
  { path: 'profile', labelKey: 'clientProfile.tab.profile', icon: <PersonIcon /> },
  { path: 'calendar', labelKey: 'clientProfile.tab.calendar', icon: <CalendarIcon /> },
  { path: 'metrics', labelKey: 'clientProfile.tab.metrics', icon: <MetricsIcon /> },
  { path: 'ai-context', labelKey: 'clientProfile.tab.aiContext', icon: <PsychologyIcon /> },
  { path: 'user-goals', labelKey: 'clientProfile.tab.userGoals', icon: <FlagIcon /> },
  { path: 'daily-norms', labelKey: 'clientProfile.tab.dailyNorms', icon: <OutlinedFlagIcon /> },
];

export const PmClientPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [client, setClient] = useState<ClientDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const tabs: PmProfileTab[] = TABS.map(({ path, labelKey, icon }) => ({ path, label: t(labelKey), icon }));
  const activeSegment = location.pathname.split('/')[4];
  const activeTab = tabs.find((tab) => tab.path === activeSegment) ?? tabs[0];
  const title = `${t('clientProfile.title')} → ${activeTab.label}`;

  useEffect(() => {
    const clientId = Number(id);
    if (!clientId) {
      setError(t('clientProfile.notFound'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    pmUsersService
      .getClient(clientId)
      .then(setClient)
      .catch((err) => {
        const message = err.response?.data?.message;
        setError(Array.isArray(message) ? message.join(', ') : message || 'Не вдалося завантажити інформацію про клієнта.');
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading || error || !client) {
    return (
      <DashboardLayout titleOverride={title}>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          {loading ? <CircularProgress /> : <Alert severity={error ? 'error' : 'info'}>{error ?? t('clientProfile.notFound')}</Alert>}
        </Box>
      </DashboardLayout>
    );
  }

  const rightAction = (
    <IconButton onClick={() => setMobileSidebarOpen(true)} color="inherit" sx={{ p: 0 }}>
      <UserAvatar path={client.general.avatar} name={client.general.name} sx={{ width: 32, height: 32 }} />
    </IconButton>
  );

  return (
    <DashboardLayout rightAction={rightAction} titleOverride={title} disableContentPadding>
      <Box
        sx={{
          pt: 1,
          px: 1.5,
          pb: 1,
          mr: { xs: 0, md: '200px' },
          height: 'calc(100vh - 52px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<PmClientDashboardTab clientId={Number(id)} client={client} />} />
            <Route path="profile" element={<PmClientProfileTab client={client} />} />
            <Route path="calendar" element={<PmClientCalendarTab clientId={Number(id)} />} />
            <Route path="metrics" element={<PmClientMetricsTab clientId={id!} />} />
            <Route path="ai-context" element={<PmClientAiContextTab clientId={Number(id)} />} />
            <Route path="user-goals" element={<PmClientGoalsTab clientId={Number(id)} />} />
            <Route path="daily-norms" element={<PmClientDailyNormsTab clientId={Number(id)} />} />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </Box>
        <PmProfileSidebar
          kind="client"
          tabs={tabs}
          activeTab={activeTab.path}
          onTabChange={(path) => navigate(`/pm/client/${id}/${path}`)}
          name={`${client.general.name || ''} ${client.general.surname || ''}`.trim()}
          avatar={client.general.avatar}
          isMobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />
      </Box>
    </DashboardLayout>
  );
};
